import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { getSpaceById } from '@core/selectors/space/space.selectors';
import { AulaState } from '@core/types/state';
import {
  Area,
  Text,
  Avatar,
  Box,
  Icons,
  IconButton,
  Tooltip,
  useTheme,
  styled,
} from '@ublend-npm/aulaui-core';
import { useMediaQuery } from '@mui/material';
import { ENTER } from '../../../../../utils/keyCodes';

const SHOW_TOOLTIP_THRESHOLD = 17;

type SpaceSelectorItemProps = {
  spaceId: string;
  avatar?: string;
  name: string;
  archived?: boolean;
  hasActivity?: boolean;
  onClick: () => void;
  onStar?: () => Promise<void>;
};

// ------------- Styled Components ------------------------
interface ListContainerProps {
  isDesktop?: boolean;
  mb?: string;
}

const ListContainer = styled(Area)<ListContainerProps>(
  ({ theme, isDesktop }) => {
    return {
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      borderRadius: '0.5rem',
      padding: 0,
      ...(isDesktop && {
        '&:hover': {
          backgroundColor: theme.palette?.action.hover,
        },
      }),
    };
  }
);

const ListItem = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  height: '62.4px',
  flexGrow: 1,
  cursor: 'pointer',
  padding: '8px 16px',
  gap: '12px',
}));

// ----------- Styled Conponents End ---------------------

const SpaceSelectorItem = ({
  spaceId,
  avatar,
  name,
  archived,
  hasActivity,
  onClick,
  onStar,
  ...props
}: SpaceSelectorItemProps) => {
  const { isStarred } = useSelector((state: AulaState) =>
    getSpaceById(state, spaceId)
  ) || { isStarred: false };
  const [isStarSaving, setIsStarSaving] = useState(false);
  const { palette, breakpoints } = useTheme();
  const isDesktop = useMediaQuery(breakpoints.up('md'));
  const handleToggleStar = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsStarSaving(true);
    // eslint-disable-next-line no-unused-expressions
    onStar && (await onStar());
    setIsStarSaving(false);
  };

  const content = (
    <ListContainer mb="0.5rem" isDesktop={isDesktop}>
      <ListItem
        onClick={onClick}
        onKeyDown={(e) => e.keyCode === ENTER && onClick()}
        tabIndex={0}
        role="button"
        data-testid="space-selector-item"
        aria-label={`Switch to the ${name} space`}
        {...props}
      >
        <Avatar
          kind="circular"
          src={avatar}
          fallbackText={name?.charAt(0).toUpperCase()}
          showBadge={hasActivity}
          badgeColor={palette.primary.main}
          sx={{
            bgcolor: '#5d536c',
            color: '#fff',
          }}
        />
        <Box style={{ flexGrow: 1, minWidth: 0 }}>
          <Text fontWeight="500" maxLines={1}>
            {name}
          </Text>
          {archived && (
            <Text fontSize="0.85rem" color="text.secondary" mt={0.25}>
              Archived
            </Text>
          )}
        </Box>
      </ListItem>

      {onStar && (
        <Box style={{ marginLeft: 'auto', padding: '0 16px' }}>
          <IconButton
            aria-label={`${isStarred ? 'Unstar' : 'Star'} the ${name} space`}
            onClick={handleToggleStar}
            size="small"
            disabled={isStarSaving}
            color="primary"
            sx={{
              '&:active': { transform: 'scale(0.9)' }, // Add a slight press effect
            }}
          >
            {isStarred || isStarSaving ? <Icons.Star /> : <Icons.StarBorder />}
          </IconButton>
        </Box>
      )}
    </ListContainer>
  );

  return name.length >= SHOW_TOOLTIP_THRESHOLD ? (
    <Tooltip position="top" title={name}>
      {content}
    </Tooltip>
  ) : (
    content
  );
};

export default React.memo(SpaceSelectorItem);
